import React, { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { close } from 'ionicons/icons';
import { ChurchStatus, type MemberStatusChange } from '@/types/member';
import { CHURCH_STATUS_LABELS } from '@/utils/labels';

interface StatusChangeModalProps {
  isOpen: boolean;
  currentStatus: ChurchStatus;
  onDismiss: () => void;
  onConfirm: (data: MemberStatusChange) => void;
  isLoading?: boolean;
}

const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  currentStatus,
  onDismiss,
  onConfirm,
  isLoading = false,
}) => {
  const [newStatus, setNewStatus] = useState<ChurchStatus>(currentStatus);
  const [reason, setReason] = useState('');
  const [decisionDate, setDecisionDate] = useState(
    new Date().toISOString().split('T')[0]!,
  );
  const [notes, setNotes] = useState('');
  const [transferChurch, setTransferChurch] = useState('');
  const [transferCity, setTransferCity] = useState('');
  const [hasTransferLetter, setHasTransferLetter] = useState(false);
  const [deathDate, setDeathDate] = useState('');

  const availableStatuses = Object.values(ChurchStatus).filter(
    (s) => s !== currentStatus && s !== ChurchStatus.LEFT,
  );

  const isTransfer = newStatus === ChurchStatus.TRANSFERRED;
  const isDeceased = newStatus === ChurchStatus.DECEASED;

  const needsReason = !isDeceased;

  const handleConfirm = () => {
    if (needsReason && !reason.trim()) return;

    const data: MemberStatusChange = {
      new_status: newStatus,
      reason: reason.trim() || (isDeceased ? 'Decedat' : ''),
      decision_date: decisionDate,
      notes: notes.trim() || null,
    };

    if (isTransfer) {
      data.transfer_church = transferChurch.trim() || null;
      data.transfer_city = transferCity.trim() || null;
      data.transfer_doc_id = hasTransferLetter ? 'cu_adeverinta' : null;
    }

    if (isDeceased) {
      data.death_date = deathDate || null;
    }

    onConfirm(data);
  };

  const handleDismiss = () => {
    setReason('');
    setNotes('');
    setTransferChurch('');
    setTransferCity('');
    setHasTransferLetter(false);
    setDeathDate('');
    onDismiss();
  };

  if (!isOpen) return null;

  return (
    <div className="crm-popup-overlay" onClick={handleDismiss}>
      <div className="crm-popup" onClick={(e) => e.stopPropagation()}>
        <div className="crm-popup__header">
          <h3 className="crm-popup__title">Schimbare Status</h3>
          <button className="crm-popup__close" onClick={handleDismiss}>
            <IonIcon icon={close} />
          </button>
        </div>

        <div className="crm-popup__body">
          <p className="crm-popup__hint">
            Status curent: <strong>{CHURCH_STATUS_LABELS[currentStatus]}</strong>
          </p>

          <div className="crm-popup__field">
            <label className="crm-popup__label">Status Nou *</label>
            <select
              className="crm-popup__input"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value as ChurchStatus)}
            >
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {CHURCH_STATUS_LABELS[status]}
                </option>
              ))}
            </select>
          </div>

          <div className="crm-popup__field">
            <label className="crm-popup__label">Data Deciziei *</label>
            <input
              className="crm-popup__input"
              type="date"
              value={decisionDate}
              onChange={(e) => setDecisionDate(e.target.value)}
            />
          </div>

          <div className="crm-popup__field">
            <label className="crm-popup__label">Motiv {needsReason ? '*' : ''}</label>
            <textarea
              className="crm-popup__textarea"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isDeceased ? 'Decedat' : 'Motivul schimbării statusului...'}
              rows={3}
            />
          </div>

          {isTransfer && (
            <>
              <div className="crm-popup__field" style={{ marginBottom: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14 }}>
                  <input
                    type="checkbox"
                    checked={hasTransferLetter}
                    onChange={(e) => setHasTransferLetter(e.target.checked)}
                    style={{ width: 16, height: 16, accentColor: '#5e81f4' }}
                  />
                  Cu adeverință de transfer
                </label>
                <span style={{ fontSize: 12, color: '#8181a5', marginTop: 4, display: 'block' }}>
                  {hasTransferLetter
                    ? 'Membru major transferat cu adeverință oficială'
                    : 'Plecat fără adeverință (ex: minor, fără cerere)'}
                </span>
              </div>
              <div className="crm-popup__row">
                <div className="crm-popup__field">
                  <label className="crm-popup__label">Biserica Destinație</label>
                  <input
                    className="crm-popup__input"
                    value={transferChurch}
                    onChange={(e) => setTransferChurch(e.target.value)}
                    placeholder="Numele bisericii"
                  />
                </div>
                <div className="crm-popup__field">
                  <label className="crm-popup__label">Localitatea</label>
                  <input
                    className="crm-popup__input"
                    value={transferCity}
                    onChange={(e) => setTransferCity(e.target.value)}
                    placeholder="Orașul / localitatea"
                  />
                </div>
              </div>
            </>
          )}

          {isDeceased && (
            <div className="crm-popup__field">
              <label className="crm-popup__label">Data Decesului</label>
              <input
                className="crm-popup__input"
                type="date"
                value={deathDate}
                onChange={(e) => setDeathDate(e.target.value)}
              />
            </div>
          )}

          <div className="crm-popup__field">
            <label className="crm-popup__label">Observații</label>
            <textarea
              className="crm-popup__textarea"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observații suplimentare (opțional)..."
              rows={2}
            />
          </div>
        </div>

        <div className="crm-popup__footer">
          <button className="crm-popup__btn crm-popup__btn--secondary" onClick={handleDismiss}>
            Anulează
          </button>
          <button
            className="crm-popup__btn crm-popup__btn--primary"
            onClick={handleConfirm}
            disabled={(needsReason && !reason.trim()) || isLoading}
          >
            {isLoading ? 'Se salvează...' : 'Confirmă'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StatusChangeModal;
